import { uniq } from 'lodash'
import type { OpenAPIV3_1 as OpenApi3 } from 'openapi-types'

import { isNonNullable } from '@subaio/utils'

import { getRef } from './getRef'

type Props = {
	schemas?: Record<string, OpenApi3.SchemaObject | OpenApi3.ReferenceObject>
	parameters?: Record<string, OpenApi3.ParameterObject | OpenApi3.ReferenceObject>
	headers?: Record<string, OpenApi3.HeaderObject | OpenApi3.ReferenceObject>
	ref: string
}

type ResponseProps = {
	responses: Record<string, OpenApi3.ReferenceObject | OpenApi3.ResponseObject>
	ref: string
	description?: string
}

export const getRefValue = ({
	schemas,
	parameters,
	headers,
	ref: schemaRef,
}: Props): OpenApi3.SchemaObject | OpenApi3.ParameterObject | OpenApi3.HeaderObject | undefined => {
	const { ref, source } = getRef(schemaRef)

	if (!ref) throw new Error(`Could not get ref from ${schemaRef}`)

	switch (source) {
		case 'schemas': {
			const definition = schemas?.[ref]
			if (definition) {
				if ('$ref' in definition)
					return getRefValue({
						schemas,
						parameters,
						headers,
						ref: definition.$ref,
					})
				if ('allOf' in definition) {
					if (definition.allOf) {
						const items = definition.allOf
							.map((item) => {
								if ('$ref' in item)
									return getRefValue({
										schemas,
										parameters,
										headers,
										ref: item.$ref,
									}) as OpenApi3.SchemaObject
								return item
							})
							.filter(isNonNullable)
						return {
							...items.reduce((a, b) => ({ ...a, ...b })),
							properties: Object.assign(
								{},
								...items.map((item) => ('properties' in item ? item.properties : null)).filter(isNonNullable),
							),
							required: uniq(
								typeof items[0].required === 'boolean'
									? []
									: items.map((item) => (item.required as Array<string>) ?? []).reduce((a, b) => [...a, ...b], []),
							),
							type: uniq(items.map((item) => ('type' in item ? item.type : undefined)).filter(isNonNullable)),
							description: definition.description ?? items.find((item) => item.description)?.description,
							$schema: schemaRef,
						} as OpenApi3.SchemaObject
					}
				}
				if ('additionalProperties' in definition) {
					if (definition.additionalProperties && typeof definition.additionalProperties !== 'boolean') {
						if ('$ref' in definition.additionalProperties && 'type' in definition.additionalProperties) {
							return {
								...definition,
								additionalProperties: {
									discriminator: definition.additionalProperties.type,
									...definition.additionalProperties,
									...getRefValue({
										schemas,
										parameters,
										headers,
										ref: definition.additionalProperties.$ref,
									}),
								},
							} as OpenApi3.SchemaObject
						}
					}
				}
			}
			return definition
		}
		case 'parameters': {
			const parameter = parameters?.[ref]
			if (parameter && '$ref' in parameter)
				return getRefValue({
					schemas,
					parameters,
					headers,
					ref: parameter.$ref,
				})
			return parameter
		}
		case 'headers': {
			const header = headers?.[ref]
			if (header && '$ref' in header) return getRefValue({ schemas, parameters, headers, ref: header.$ref })
			return header
		}
	}
	throw new Error(`Unknown source ${source} for ref ${ref} gathered from ${schemaRef}`)
}

export const getResponseRefValue = ({
	responses,
	ref,
	description,
}: ResponseProps): OpenApi3.ResponseObject | undefined => {
	const response = responses[getRef(ref).ref]
	if (!response) return undefined
	if ('$ref' in response) return getResponseRefValue({ responses, ref: response.$ref, description })
	if (description !== undefined) return { ...response, description }
	return response
}
